import { createContext, useContext, type PropsWithChildren } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  AccountProfileUpdateInput,
  OrganizationInvitationAcceptanceInput,
  OrganizationInvitationAcceptanceResult,
  OrganizationInvitationConfirmationInput,
  LoginInput,
  PasskeyLoginStartResult,
  PasskeyRegistrationStartResult,
  SessionUser,
  SignupCompleteResult,
  SignupConfirmationInput,
  SignupInput,
  SignupStartResult,
} from '@cleopatra/contracts';
import { api, jsonBody } from './api';
import { createPasskey, getPasskey } from './passkeys';

interface AuthContextValue {
  user: SessionUser | undefined;
  loading: boolean;
  login: (input: LoginInput) => Promise<SessionUser>;
  loginWithPasskey: (email: string) => Promise<SessionUser>;
  signup: (input: SignupInput) => Promise<SignupStartResult>;
  confirmSignup: (input: SignupConfirmationInput) => Promise<SignupCompleteResult>;
  acceptInvitation: (
    token: string,
    input: OrganizationInvitationAcceptanceInput,
  ) => Promise<OrganizationInvitationAcceptanceResult>;
  confirmInvitation: (
    token: string,
    input: OrganizationInvitationConfirmationInput,
  ) => Promise<OrganizationInvitationAcceptanceResult>;
  setupPasskey: () => Promise<void>;
  skipPasskeySetup: () => Promise<void>;
  logout: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  updateProfile: (input: AccountProfileUpdateInput) => Promise<SessionUser>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function replaceTenantScopedCache(queryClient: QueryClient, user: SessionUser): void {
  queryClient.getMutationCache().clear();
  queryClient.removeQueries({
    predicate: ({ queryKey }) => !(queryKey.length === 1 && queryKey[0] === 'session'),
  });
  queryClient.setQueryData(['session'], user);
}

export function discardAuthenticatedCache(queryClient: QueryClient): void {
  queryClient.getMutationCache().clear();
  queryClient.removeQueries({
    predicate: ({ queryKey }) => !(queryKey.length === 1 && queryKey[0] === 'session'),
  });
  queryClient.setQueryData(['session'], null);
}

export function updateCachedOrganizationName(
  queryClient: QueryClient,
  tenantId: string,
  name: string,
): void {
  queryClient.setQueryData<SessionUser | null>(['session'], (current) => {
    if (!current) return current;
    return {
      ...current,
      activeTenant:
        current.activeTenant.id === tenantId
          ? { ...current.activeTenant, name }
          : current.activeTenant,
      tenants: current.tenants.map((tenant) =>
        tenant.id === tenantId ? { ...tenant, name } : tenant,
      ),
    };
  });
}

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const session = useQuery<SessionUser | null>({
    queryKey: ['session'],
    queryFn: () => api<SessionUser>('/auth/me'),
    retry: false,
    staleTime: 30_000,
  });
  const loginMutation = useMutation({
    mutationFn: (input: LoginInput) =>
      api<SessionUser>('/auth/login', { method: 'POST', ...jsonBody(input) }),
    onSuccess: (user) => queryClient.setQueryData(['session'], user),
  });
  const signupMutation = useMutation({
    mutationFn: (input: SignupInput) =>
      api<SignupStartResult>('/auth/signup', { method: 'POST', ...jsonBody(input) }),
  });
  const passkeyLoginMutation = useMutation({
    mutationFn: async (email: string) => {
      const started = await api<PasskeyLoginStartResult>('/auth/passkeys/login/start', {
        method: 'POST',
        ...jsonBody({ email }),
      });
      const credential = await getPasskey(started.credentialRequestOptions);
      return api<SessionUser>('/auth/passkeys/login/complete', {
        method: 'POST',
        ...jsonBody({ email, credential }),
      });
    },
    onSuccess: (user) => queryClient.setQueryData(['session'], user),
  });
  const passkeySetupMutation = useMutation({
    mutationFn: async () => {
      const started = await api<PasskeyRegistrationStartResult>(
        '/auth/passkeys/registration/start',
        { method: 'POST' },
      );
      const credential = await createPasskey(started.credentialCreationOptions);
      await api<{ ok: true }>('/auth/passkeys/registration/complete', {
        method: 'POST',
        ...jsonBody({ credential }),
      });
    },
  });
  const confirmSignupMutation = useMutation({
    mutationFn: (input: SignupConfirmationInput) =>
      api<SignupCompleteResult>('/auth/signup/confirm', {
        method: 'POST',
        ...jsonBody(input),
      }),
    onSuccess: (result) => queryClient.setQueryData(['session'], result.user),
  });
  const logoutMutation = useMutation({
    mutationFn: () => api<{ ok: true }>('/auth/session', { method: 'DELETE' }),
    onSuccess: () => discardAuthenticatedCache(queryClient),
  });
  const acceptInvitationMutation = useMutation({
    mutationFn: ({
      token,
      input,
    }: {
      token: string;
      input: OrganizationInvitationAcceptanceInput;
    }) =>
      api<OrganizationInvitationAcceptanceResult>('/auth/invitations/accept', {
        method: 'POST',
        ...jsonBody({ token, ...input }),
      }),
    onSuccess: (result) => {
      if (result.status === 'joined') replaceTenantScopedCache(queryClient, result.user);
    },
  });
  const confirmInvitationMutation = useMutation({
    mutationFn: ({
      token,
      input,
    }: {
      token: string;
      input: OrganizationInvitationConfirmationInput;
    }) =>
      api<OrganizationInvitationAcceptanceResult>('/auth/invitations/confirm', {
        method: 'POST',
        ...jsonBody({ token, ...input }),
      }),
    onSuccess: (result) => {
      if (result.status === 'joined') replaceTenantScopedCache(queryClient, result.user);
    },
  });
  const switchMutation = useMutation({
    mutationFn: (tenantId: string) =>
      api<SessionUser>('/auth/switch-tenant', { method: 'POST', ...jsonBody({ tenantId }) }),
    onSuccess: (user) => replaceTenantScopedCache(queryClient, user),
  });
  const updateProfileMutation = useMutation({
    mutationFn: (input: AccountProfileUpdateInput) =>
      api<SessionUser>('/auth/profile', { method: 'PATCH', ...jsonBody(input) }),
    onSuccess: (user) => queryClient.setQueryData(['session'], user),
  });

  return (
    <AuthContext.Provider
      value={{
        user: session.data ?? undefined,
        loading: session.isLoading,
        login: loginMutation.mutateAsync,
        loginWithPasskey: passkeyLoginMutation.mutateAsync,
        signup: signupMutation.mutateAsync,
        confirmSignup: confirmSignupMutation.mutateAsync,
        acceptInvitation: (token, input) => acceptInvitationMutation.mutateAsync({ token, input }),
        confirmInvitation: (token, input) =>
          confirmInvitationMutation.mutateAsync({ token, input }),
        setupPasskey: passkeySetupMutation.mutateAsync,
        skipPasskeySetup: () =>
          api<{ ok: true }>('/auth/passkeys/registration', { method: 'DELETE' }).then(() => {}),
        logout: async () => {
          await logoutMutation.mutateAsync();
        },
        switchTenant: async (tenantId) => {
          await switchMutation.mutateAsync(tenantId);
        },
        updateProfile: (input) => updateProfileMutation.mutateAsync(input),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within AuthProvider.');
  return value;
}

export function canWriteRole(role: string | undefined): boolean {
  return Boolean(role && ['owner', 'attorney', 'staff', 'advocate'].includes(role));
}

export function useCanWrite(): boolean {
  const { user } = useAuth();
  return canWriteRole(user?.activeTenant.role);
}
